import { round } from "./state.js";
import { MAX_TRIES, TIER_TEXT_COLOR } from "./hint.js";
import { loadImage, roundRect, wrapText } from "./util.js";

const toastEl = document.getElementById("toast");
let toastTimer = null;

export function showToast(msg, ok = true) {
  toastEl.innerHTML = `${ok ? `<span class="check">✓</span>` : ""}<span>${msg}</span>`;
  toastEl.classList.add("show");
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toastEl.classList.remove("show"), 1700);
}

function drawArrow(ctx, cx, cy, size, kind) {
  const vbW = 28, vbH = 34;
  const scale = size / vbW;
  const drawnW = vbW * scale, drawnH = vbH * scale;
  const x = cx - drawnW / 2, y = cy - drawnH / 2;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);

  const grad = ctx.createLinearGradient(0, 0, 0, vbH);
  if (kind === "down") {
    grad.addColorStop(0, "#ff9d94");
    grad.addColorStop(0.48, "#da3c2f");
    grad.addColorStop(0.49, "#8b1e16");
    grad.addColorStop(1, "#c82c22");
  } else {
    grad.addColorStop(0, "#b5ee9a");
    grad.addColorStop(0.48, "#38a14a");
    grad.addColorStop(0.49, "#176020");
    grad.addColorStop(1, "#3aa147");
  }
  ctx.fillStyle = grad;
  ctx.strokeStyle = kind === "down" ? "#5a1510" : "#0f3015";
  ctx.lineWidth = 1;
  ctx.lineJoin = "round";

  ctx.beginPath();
  let p;
  if (kind === "up")          p = [[14,1],[27,14],[20,14],[20,33],[8,33],[8,14],[1,14]];
  else if (kind === "down")   p = [[14,33],[27,20],[20,20],[20,1],[8,1],[8,20],[1,20]];
  else                        p = [[3,17],[11,25],[25,7],[22,4],[11,15],[6,10]];
  p.forEach(([px, py], i) => { i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py); });
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  ctx.globalAlpha = 0.35;
  const hi = ctx.createLinearGradient(0, 0, 0, vbH / 2);
  hi.addColorStop(0, "#ffffff");
  hi.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = hi;
  ctx.fill();
  ctx.restore();
}

async function generateShareImage() {
  const { item, guesses, won } = round;
  const W = 1080, H = 1200;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Desktop-style background
  ctx.fillStyle = "#eaeaea";
  ctx.fillRect(0, 0, W, H);

  // Window bounds
  const winX = 40, winY = 40, winW = W - 80, winH = H - 80;
  const radius = 14;
  const tbH = 54;

  // Drop shadow + white fill
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.28)";
  ctx.shadowBlur = 28;
  ctx.shadowOffsetY = 10;
  ctx.fillStyle = "#ffffff";
  roundRect(ctx, winX, winY, winW, winH, radius);
  ctx.fill();
  ctx.restore();

  // Pinstripe title bar
  ctx.save();
  roundRect(ctx, winX, winY, winW, winH, radius);
  ctx.clip();
  const tbGrad = ctx.createLinearGradient(0, winY, 0, winY + tbH);
  tbGrad.addColorStop(0, "#ececec");
  tbGrad.addColorStop(1, "#c6c6c6");
  ctx.fillStyle = tbGrad;
  ctx.fillRect(winX, winY, winW, tbH);
  ctx.strokeStyle = "rgba(0,0,0,0.05)";
  ctx.lineWidth = 1;
  for (let yy = winY + 2; yy < winY + tbH; yy += 2) {
    ctx.beginPath();
    ctx.moveTo(winX, yy + 0.5); ctx.lineTo(winX + winW, yy + 0.5);
    ctx.stroke();
  }
  ctx.restore();

  // Title bar divider + outline
  ctx.strokeStyle = "#7a7a7a";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(winX, winY + tbH + 0.5);
  ctx.lineTo(winX + winW, winY + tbH + 0.5);
  ctx.stroke();
  ctx.strokeStyle = "#5a5a5a";
  ctx.lineWidth = 1.5;
  roundRect(ctx, winX, winY, winW, winH, radius);
  ctx.stroke();

  // Traffic-light red dot
  const dotX = winX + 24, dotY = winY + tbH / 2, dotR = 11;
  const dotGrad = ctx.createRadialGradient(dotX - 3, dotY - 3, 1, dotX, dotY, dotR);
  dotGrad.addColorStop(0, "#ff9b97");
  dotGrad.addColorStop(0.55, "#ff5f56");
  dotGrad.addColorStop(1, "#d63a31");
  ctx.fillStyle = dotGrad;
  ctx.beginPath();
  ctx.arc(dotX, dotY, dotR, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "rgba(0,0,0,0.35)";
  ctx.lineWidth = 1;
  ctx.stroke();

  // Title bar text
  ctx.fillStyle = "#3a3a3a";
  ctx.font = `700 22px "Lucida Grande", "Helvetica Neue", Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("Round Result", winX + winW / 2, winY + tbH / 2 + 1);
  ctx.textBaseline = "alphabetic";

  // Content starts below title bar
  const contentTop = winY + tbH;

  // eBay logo
  let y = contentTop + 20;
  try {
    const logo = await loadImage("/logo.png");
    const targetH = 80;
    const targetW = logo.width * (targetH / logo.height);
    ctx.drawImage(logo, (W - targetW) / 2, y, targetW, targetH);
    y += targetH + 10;
  } catch {
    ctx.fillStyle = "#121212";
    ctx.font = `800 72px "Helvetica Neue", Arial, sans-serif`;
    ctx.fillText("eGuessr", W / 2, y + 65);
    y += 80;
  }

  // Subtitle
  ctx.fillStyle = "#707070";
  ctx.font = `500 24px "Helvetica Neue", Arial, sans-serif`;
  ctx.fillText("Wordle for eBay listings", W / 2, y + 24);
  y += 40;

  // Item photo
  const imgUrl = (round.images && round.images[round.activeImg || 0]) || item.imgUrl;
  const photoSize = 180;
  const photoX = (W - photoSize) / 2;
  const photoY = y + 14;
  try {
    const itemImg = await loadImage(`/api/proxy-img?url=${encodeURIComponent(imgUrl)}`);
    ctx.save();
    roundRect(ctx, photoX, photoY, photoSize, photoSize, 8);
    ctx.clip();
    const ratio = Math.min(photoSize / itemImg.width, photoSize / itemImg.height);
    const drawW = itemImg.width * ratio, drawH = itemImg.height * ratio;
    ctx.drawImage(itemImg, photoX + (photoSize - drawW) / 2, photoY + (photoSize - drawH) / 2, drawW, drawH);
    ctx.restore();
  } catch {}
  y = photoY + photoSize + 20;

  // Item title
  ctx.fillStyle = "#121212";
  ctx.font = `700 26px "Helvetica Neue", Arial, sans-serif`;
  const titleLines = wrapText(ctx, item.title, W - 220, 2);
  titleLines.forEach((line, i) => ctx.fillText(line, W / 2, y + i * 32 + 20));
  y += titleLines.length * 32 + 30;

  // Guess rows
  ctx.textAlign = "left";
  const rowsTop = y + 10;
  const rowW = winW - 80, rowH = 56, rowGap = 6;
  const rowX = winX + 40;
  for (let i = 0; i < MAX_TRIES; i++) {
    const rowY = rowsTop + i * (rowH + rowGap);
    const g = guesses[i];
    const filled = !!g;
    const isFinal = filled && i === guesses.length - 1;
    const highlight = isFinal ? (won ? "win" : "lose") : null;

    if (highlight === "win")       ctx.fillStyle = "#e3f4df";
    else if (highlight === "lose") ctx.fillStyle = "#fce8e5";
    else                           ctx.fillStyle = "#ffffff";
    roundRect(ctx, rowX, rowY, rowW, rowH, 6);
    ctx.fill();
    if (highlight === "win")       { ctx.strokeStyle = "#2f8f3b"; ctx.lineWidth = 2.5; }
    else if (highlight === "lose") { ctx.strokeStyle = "#a81f12"; ctx.lineWidth = 2.5; }
    else {
      ctx.strokeStyle = filled ? "#8a8a8a" : "#d0d0d0";
      ctx.lineWidth = filled ? 2 : 1.5;
    }
    if (!filled) ctx.setLineDash([6, 5]);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = "#707070";
    ctx.font = `700 20px "Helvetica Neue", Arial, sans-serif`;
    ctx.textBaseline = "middle";
    ctx.fillText(String(i + 1), rowX + 20, rowY + rowH / 2);

    if (filled) {
      ctx.fillStyle = "#121212";
      ctx.font = `700 26px "Helvetica Neue", Arial, sans-serif`;
      ctx.fillText(`$${g.guess.toFixed(2)}`, rowX + 60, rowY + rowH / 2);

      ctx.fillStyle = TIER_TEXT_COLOR[g.hint.tier] || "#707070";
      ctx.font = `800 20px "Helvetica Neue", Arial, sans-serif`;
      ctx.textAlign = "right";
      ctx.fillText(g.hint.text.toUpperCase(), rowX + rowW - 72, rowY + rowH / 2);
      ctx.textAlign = "left";

      const kind = g.hint.tier === "exact" ? "check"
                  : (g.hint.cls === "too-high" ? "down" : "up");
      drawArrow(ctx, rowX + rowW - 42, rowY + rowH / 2, 36, kind);
    } else {
      ctx.fillStyle = "#d0d0d0";
      ctx.font = `700 24px "Helvetica Neue", Arial, sans-serif`;
      ctx.fillText("—", rowX + 60, rowY + rowH / 2);
    }
    ctx.textBaseline = "alphabetic";
  }
  ctx.textAlign = "center";

  // Small tries count
  const rowsBottom = rowsTop + MAX_TRIES * rowH + (MAX_TRIES - 1) * rowGap;
  ctx.fillStyle = "#707070";
  ctx.font = `500 22px "Helvetica Neue", Arial, sans-serif`;
  const triesText = won
    ? `${guesses.length} / ${MAX_TRIES} ${guesses.length === 1 ? "try" : "tries"}`
    : `${MAX_TRIES} / ${MAX_TRIES} — couldn't crack it`;
  ctx.fillText(triesText, W / 2, rowsBottom + 34);

  // URL
  const footerY = winY + winH - 36;
  ctx.fillStyle = "#1a4a99";
  ctx.font = `700 24px "Helvetica Neue", Arial, sans-serif`;
  const host = window.location.host || "eguessr.com";
  ctx.fillText(host, W / 2, footerY);

  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

export async function shareResult() {
  let blob = null;
  try { blob = await generateShareImage(); } catch (e) { console.warn("image gen failed:", e); }
  if (!blob) { showToast("Could not build image", false); return; }

  try {
    await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
    showToast("Copied to Clipboard");
    return;
  } catch (e) {
    console.warn("clipboard image failed:", e);
  }

  // Fallback: download
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "eguessr-result.png";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  showToast("Image saved");
}
